import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

const severityToAlert = (severity) => {
  if (severity === 'high') return 'error';
  if (severity === 'medium') return 'warning';
  return 'info';
};

const statusColor = (status) => {
  if (status === 'approved') return 'success';
  if (status === 'approved_with_warnings') return 'warning';
  if (status === 'needs_fix' || status === 'blocked') return 'error';
  return 'default';
};

const riskColor = (risk) => {
  if (risk === 'low') return 'success';
  if (risk === 'medium') return 'warning';
  if (risk === 'high') return 'error';
  return 'default';
};

const verificationChip = (review) => {
  if (review?.review_mode === "llm") return { label: "LLM verified", color: "success" };
  if (review?.review_mode === "llm_repaired") return { label: "LLM repaired", color: "info" };
  if (review?.review_mode === "llm_budget_exhausted") return { label: "LLM budget exhausted", color: "error" };
  if (review?.review_mode === "llm_incomplete") return { label: "LLM incomplete", color: "warning" };
  return { label: "Heuristic only", color: "warning" };
};

const buildDebugFilename = (targetFilePath) => {
  const safeName = (targetFilePath || 'modularise_review')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `${safeName || 'modularise_review'}_debug.txt`;
};

const ImportZipModulariseReviewDialog = ({
  open,
  onClose,
  onRunReview,
  loading,
  error,
  review,
  targetFilePath,
}) => {
  const findings = Array.isArray(review?.findings) ? review.findings : [];
  const strengths = Array.isArray(review?.strengths) ? review.strengths : [];
  const nextActions = Array.isArray(review?.next_actions)
    ? review.next_actions
    : [];
  const metadata = review?.metadata || {};
  const verification = verificationChip(review);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugCopyStatus, setDebugCopyStatus] = useState('');
  const debugFileText = useMemo(() => (review?.debug_file_text || ''), [review]);
  const debugFilename = useMemo(() => buildDebugFilename(targetFilePath), [targetFilePath]);

  const handleCopyDebug = async () => {
    if (!debugFileText) return;
    try {
      await navigator.clipboard.writeText(debugFileText);
      setDebugCopyStatus('Copied debug file to clipboard');
      setTimeout(() => setDebugCopyStatus(''), 2000);
    } catch (e) {
      setDebugCopyStatus('Copy failed');
      setTimeout(() => setDebugCopyStatus(''), 2500);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Verify Modularisation</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Target file: <code>{targetFilePath || '(not selected)'}</code>
          </Typography>

          {loading ? <LinearProgress /> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}

          {review ? (
            <>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  size="small"
                  label={`Status: ${review.status || 'unknown'}`}
                  color={statusColor(review.status)}
                />
                <Chip
                  size="small"
                  label={`Risk: ${review.risk_level || 'unknown'}`}
                  color={riskColor(review.risk_level)}
                />
                <Chip
                  size="small"
                  label={
                    review.approved_to_save ? 'Approved to save' : 'Fix before save'
                  }
                  color={review.approved_to_save ? 'success' : 'error'}
                />
                <Chip
                  size="small"
                  label={verification.label}
                  color={verification.color}
                  variant={review?.llm_verified ? "filled" : "outlined"}
                />
                <Chip
                  size="small"
                  label={`Mode: ${review.review_mode || 'unknown'}`}
                  variant="outlined"
                />
              </Stack>

              <Alert severity={review.approved_to_save ? 'success' : 'warning'}>
                {review.summary || 'Review completed.'}
              </Alert>

              {!review?.llm_verified ? (
                <Alert severity={review?.review_mode === 'llm_budget_exhausted' ? 'error' : 'warning'}>
                  {review?.review_mode === 'llm_budget_exhausted'
                    ? 'The LLM reviewer exhausted its token budget before producing output. This result is heuristic-only.'
                    : review?.review_mode === 'llm_incomplete'
                      ? 'The LLM reviewer returned incomplete output. This result is heuristic-only.'
                      : 'This result was not fully verified by the LLM reviewer. Treat it as a heuristic safety check and preview the impacted files before saving.'}
                </Alert>
              ) : null}

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Review summary</Typography>
                  <Typography variant="body2">
                    Behavior preserved:{' '}
                    <strong>{review.behavior_preserved ? 'Yes' : 'Needs review'}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Modularisation quality:{' '}
                    <strong>{review.modularisation_quality || 'unknown'}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Reviewed files: <strong>{metadata.file_count || 0}</strong>
                  </Typography>
                  {review.llm_error ? (
                    <Alert severity="info">
                      LLM fallback note: {review.llm_error}
                    </Alert>
                  ) : null}
                </Stack>
              </Paper>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle2">Findings</Typography>
                {findings.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No findings reported.
                  </Typography>
                ) : (
                  findings.map((item, idx) => (
                    <Alert key={`${item.file || 'finding'}-${idx}`} severity={severityToAlert(item.severity)}>
                      <strong>{item.file || 'General'}:</strong> {item.message || ''}
                    </Alert>
                  ))
                )}
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2">Strengths</Typography>
                {strengths.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No strengths reported.
                  </Typography>
                ) : (
                  strengths.map((item, idx) => (
                    <Typography key={`strength-${idx}`} variant="body2">
                      • {item}
                    </Typography>
                  ))
                )}
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2">Next actions</Typography>
                {nextActions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No next actions reported.
                  </Typography>
                ) : (
                  nextActions.map((item, idx) => (
                    <Typography key={`next-${idx}`} variant="body2">
                      • {item}
                    </Typography>
                  ))
                )}
              </Stack>

              {debugFileText ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="outlined" onClick={() => setDebugOpen(true)}>
                    Show Debug File
                  </Button>
                  <Button variant="text" onClick={handleCopyDebug}>
                    Copy Debug File
                  </Button>
                  {debugCopyStatus ? (
                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      {debugCopyStatus}
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}

              {Array.isArray(metadata.reviewed_paths) && metadata.reviewed_paths.length ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Reviewed paths
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1, maxHeight: 180, overflow: 'auto' }}>
                    {metadata.reviewed_paths.map((path) => (
                      <Typography key={path} variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {path}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              ) : null}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Run the review to verify that the modularisation preserved behavior and wiring before save.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {debugFileText ? (
          <Button onClick={() => setDebugOpen(true)} variant="text">
            Show Debug File
          </Button>
        ) : null}
        <Button onClick={onRunReview} variant="outlined" disabled={loading || !targetFilePath}>
          Re-run review
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <Dialog open={debugOpen} onClose={() => setDebugOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Debug file</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              File: <code>{debugFilename}</code>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Copy this full reviewer input/output back into chat for troubleshooting.
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, maxHeight: '70vh', overflow: 'auto' }}>
              <Typography component="pre" variant="body2" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                {debugFileText || 'No debug file available.'}
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyDebug} variant="outlined" disabled={!debugFileText}>
            Copy Debug File
          </Button>
          <Button onClick={() => setDebugOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ImportZipModulariseReviewDialog;
